"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "~/bridge/ui/Link";
import { useAppContext } from "../../app-context/provider";
import { useCart } from "~/components/cart/useCart";
import { useKlaviyo } from "../aplia/Klaviyo";

// Public tenant identifier – not a secret
const TENANT =
  process.env.NEXT_PUBLIC_CRYSTALLIZE_TENANT_IDENTIFIER ?? "herreapoteket-41";
const SEARCH_API_URL = `https://api.crystallize.com/${TENANT}/search`;

// Minimal query – only fields used by the overlay UI
const SEARCH_QUERY = `
query OverlaySearch($language: String!, $term: String!) {
  search(language: $language, filter: { searchTerm: $term }, first: 40) {
    edges {
      node {
        name
        path
        type
        topics { name }
        ... on Product {
          matchingVariant {
            sku
            price
            priceVariants { identifier price }
            images { url }
          }
        }
      }
    }
  }
}
`;

// ─── Types ──────────────────────────────────────────────────────────────────

type SearchHit = {
  name: string;
  path: string;
  type: "product" | "document" | "folder";
  topics: { name: string }[];
  matchingVariant?: {
    sku: string;
    price: number;
    priceVariants: { identifier: string; price: number }[];
    images: { url: string }[];
  };
  intentScore?: number;
};

// ─── Levenshtein distance for typo-tolerance ────────────────────────────────

function getEditDistance(a: string, b: string): number {
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix: number[][] = Array.from({ length: b.length + 1 }, (_, j) =>
    Array.from({ length: a.length + 1 }, (_, i) =>
      j === 0 ? i : i === 0 ? j : 0,
    ),
  );
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + cost,
      );
    }
  }
  return matrix[b.length][a.length];
}

/**
 * The search index returns one edge per matching variant, so a product with
 * several variants comes back two or three times over - a "cerave" search
 * returns 40 edges covering only 19 products. That duplicated rows in the
 * dropdown, collided on the React key (rows are keyed by path), and wasted
 * most of the 15-row cap on repeats.
 *
 * Keep one row per path. Of the duplicates we keep the cheapest matching
 * variant, since the row shows a price and adds that exact sku to the cart -
 * picking arbitrarily could offer a three-pack at triple the price when the
 * single is what the shopper searched for.
 */
function dedupeByPath(hits: SearchHit[]): SearchHit[] {
  const best = new Map<string, SearchHit>();
  for (const hit of hits) {
    const seen = best.get(hit.path);
    if (!seen) {
      best.set(hit.path, hit);
      continue;
    }
    const price = hit.matchingVariant?.price;
    const seenPrice = seen.matchingVariant?.price;
    if (price !== undefined && (seenPrice === undefined || price < seenPrice)) {
      best.set(hit.path, hit);
    }
  }
  return [...best.values()];
}

// ─── Multi-field scoring (port of new_search.ts algorithm) ──────────────────

function scoreResults(hits: SearchHit[], term: string): SearchHit[] {
  const termLower = term.toLowerCase().trim();
  const termStripped = termLower.replace(/[^a-z0-9æøå]/g, "");
  const tokens = termLower
    .replace(/[-_.,]/g, " ")
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);

  return hits
    .map((hit) => {
      let score = 0;
      const nameLower = hit.name.toLowerCase();
      const pathLower = hit.path.toLowerCase();
      const topicsStr = hit.topics.map((t) => t.name.toLowerCase()).join(" ");
      const nameStripped = nameLower.replace(/[^a-z0-9æøå]/g, "");
      const pathStripped = pathLower.replace(/[^a-z0-9æøå]/g, "");
      const topicsStripped = topicsStr.replace(/[^a-z0-9æøå\s]/g, "");

      // Whole-name match bonuses
      if (nameStripped === termStripped) score += 10000;
      else if (nameStripped.startsWith(termStripped)) score += 3000;
      else if (nameStripped.includes(termStripped)) score += 1500;

      let tokensMatched = 0;
      let titleTokensMatched = 0;

      tokens.forEach((token) => {
        const tokenStripped = token.replace(/[^a-z0-9æøå]/g, "");
        let matchedAnywhere = false;

        if (nameLower.includes(token) || nameStripped.includes(tokenStripped)) {
          score += 1000;
          titleTokensMatched++;
          matchedAnywhere = true;
        } else if (
          pathLower.includes(token) ||
          pathStripped.includes(tokenStripped) ||
          topicsStr.includes(token) ||
          topicsStripped.includes(tokenStripped)
        ) {
          score += 500;
          matchedAnywhere = true;
        }

        if (matchedAnywhere) {
          tokensMatched++;
        } else if (token.length > 3) {
          const titleWords = nameLower.split(/[^\wæøåÆØÅ]+/);
          let fuzzy = false;
          for (const w of titleWords) {
            if (w.length > 3 && getEditDistance(token, w) <= 2) {
              score += 600;
              titleTokensMatched++;
              fuzzy = true;
              break;
            }
          }
          if (!fuzzy) {
            const otherWords = [
              ...pathLower.split(/[/\-]+/),
              ...topicsStr.split(/[^\wæøåÆØÅ]+/),
            ];
            for (const w of otherWords) {
              if (w.length > 3 && getEditDistance(token, w) <= 2) {
                score += 300;
                fuzzy = true;
                break;
              }
            }
          }
          if (fuzzy) tokensMatched++;
        }
      });

      if (tokens.length > 1) {
        if (titleTokensMatched >= tokens.length) score += 8000;
        else if (tokensMatched >= tokens.length) score += 4000;
      }

      // Type boost
      if (hit.type === "folder") score += 200;
      else if (hit.type === "product") score += 100;
      else if (hit.type === "document") score -= 800;

      return { ...hit, intentScore: score };
    })
    .sort((a, b) => (b.intentScore ?? 0) - (a.intentScore ?? 0));
}

// ─── Text highlight ──────────────────────────────────────────────────────────

function HighlightedText({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) {
  const words = highlight
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 1);
  if (!words.length) return <span>{text}</span>;
  const regex = new RegExp(
    `(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <strong key={i} className="text-black font-bold">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
}

// ─── Price helpers ───────────────────────────────────────────────────────────

function getPrice(hit: SearchHit) {
  const pv = hit.matchingVariant?.priceVariants;
  if (!pv?.length)
    return { default: hit.matchingVariant?.price, sales: undefined };
  const def = pv.find((p) => p.identifier === "default");
  const sal = pv.find(
    (p) => p.identifier === "sales" && p.price < (def?.price ?? Infinity),
  );
  return {
    default: def?.price ?? hit.matchingVariant?.price,
    sales: sal?.price,
  };
}

// ─── Type badge labels ───────────────────────────────────────────────────────

function TypeLabel({ type }: { type: SearchHit["type"] }) {
  const labels: Record<string, string> = {
    product: "Produkt",
    document: "Blogg",
    folder: "Kategori",
  };
  return (
    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-green-800 bg-green-50 px-2 py-0.5 rounded-sm border border-green-200">
      {labels[type] ?? type}
    </span>
  );
}

// ─── Cart button states ───────────────────────────────────────────────────────

function CartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m12-9l2 9m-9-4h4"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

// ─── Main overlay component ───────────────────────────────────────────────────

export function SearchOverlay({
  isOpen,
  onClose,
  popularSearchTerms,
}: {
  isOpen: boolean;
  onClose: () => void;
  popularSearchTerms?: string[];
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [addingToCart, setAddingToCart] = useState<
    Record<string, "idle" | "loading" | "success">
  >({});

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cacheRef = useRef<Record<string, SearchHit[]>>({});
  const abortRef = useRef<AbortController | null>(null);

  const {
    state: appContextState,
    dispatch: contextDispatch,
    path,
  } = useAppContext();
  const cart = useCart();
  const { getKlaviyoUser } = useKlaviyo();

  // Lock body scroll and auto-focus input
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    // Small delay ensures the DOM is ready before focusing
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Debounced search with in-memory cache
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length <= 1) {
      setResults([]);
      return;
    }
    if (cacheRef.current[term]) {
      setResults(cacheRef.current[term]);
      return;
    }
    const timer = setTimeout(() => performSearch(term), 150);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset keyboard selection when results change
  useEffect(() => setSelectedIndex(-1), [results]);

  // Reset state when overlay closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  const performSearch = useCallback(
    async (term: string) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setIsSearching(true);
      try {
        const res = await fetch(SEARCH_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: abortRef.current.signal,
          body: JSON.stringify({
            query: SEARCH_QUERY,
            variables: { language: appContextState.language, term },
          }),
        });
        const json = await res.json();
        if (json.data?.search?.edges) {
          const raw: SearchHit[] = json.data.search.edges.map(
            (e: any) => e.node,
          );
          const filtered = dedupeByPath(
            raw.filter((h) => !h.path.toLowerCase().includes("dameapoteket")),
          );
          const scored = scoreResults(filtered, term).slice(0, 15);
          cacheRef.current[term] = scored;
          setResults(scored);
        }
      } catch (e: any) {
        if (e.name !== "AbortError") console.error("Search error:", e);
      } finally {
        setIsSearching(false);
      }
    },
    [appContextState.language],
  );

  const handleNavigate = useCallback(
    (hitPath: string) => {
      window.location.href = path(hitPath);
      onClose();
    },
    [path, onClose],
  );

  const handleShowAll = useCallback(() => {
    window.location.href = path(`/search?q=${encodeURIComponent(searchTerm)}`);
    onClose();
  }, [path, searchTerm, onClose]);

  const handleQuickAdd = useCallback(
    async (e: React.MouseEvent, hit: SearchHit) => {
      e.stopPropagation();
      const sku = hit.matchingVariant?.sku;
      if (!sku) return;
      const user = getKlaviyoUser();
      setAddingToCart((prev) => ({ ...prev, [sku]: "loading" }));
      const result = await cart.add(sku, 1, user?.email);
      if (result.success) {
        contextDispatch.addItemsToCart([
          {
            id: sku,
            name: hit.name,
            sku,
            images:
              hit.matchingVariant?.images?.map((img) => ({
                // The search index only returns the url, so use it as the key
                // and fall back to the product name for alt text.
                key: img.url,
                url: img.url,
                altText: hit.name,
                variants: [],
              })) ?? [],
            priceVariants: Object.fromEntries(
              (hit.matchingVariant?.priceVariants ?? []).map((pv) => [
                pv.identifier,
                {
                  identifier: pv.identifier,
                  name: pv.identifier,
                  value: pv.price,
                  currency: appContextState.currency,
                  priceFor: {
                    //@ts-ignore
                    identifier: pv.priceFor?.identifier ?? pv.identifier,
                    //@ts-ignore
                    price: pv.priceFor?.price ?? pv.price,
                  },
                },
              ]),
            ),
            stockLocations: {},
            attributes: {},
            isDefault: true,
            outofstock: { content: { value: false } },
          },
        ]);
        setAddingToCart((prev) => ({ ...prev, [sku]: "success" }));
      } else {
        setAddingToCart((prev) => ({ ...prev, [sku]: "idle" }));
      }
      setTimeout(() => {
        setAddingToCart((prev) => {
          const next = { ...prev };
          delete next[sku];
          return next;
        });
      }, 1500);
    },
    [cart, contextDispatch, getKlaviyoUser, appContextState.currency],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const total = results.length + (results.length > 0 ? 1 : 0); // +1 for show-all row
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((p) => Math.min(p + 1, total - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((p) => Math.max(p - 1, -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleNavigate(results[selectedIndex].path);
        } else if (searchTerm.trim().length > 1) {
          handleShowAll();
        }
      }
    },
    [results, selectedIndex, searchTerm, handleNavigate, handleShowAll],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  const showZeroState = searchTerm.trim().length <= 1;
  const showResults = results.length > 0 && searchTerm.trim().length > 1;
  const showNoResults =
    !isSearching && results.length === 0 && searchTerm.trim().length > 1;

  return (
    <div
      className="fixed inset-0 z-200 flex items-start justify-center px-3 pt-3 sm:pt-18 sm:px-6"
      onClick={handleBackdropClick}
      // Desktop: blur backdrop. Mobile: solid overlay (perf)
      style={{
        backgroundColor: "rgba(14, 14, 14, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <div
        ref={containerRef}
        className="w-full max-w-[680px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "calc(100dvh - 1.5rem)", minHeight: "120px" }}
        role="dialog"
        aria-modal="true"
        aria-label="Søk"
      >
        {/* ── Input bar ─────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <div className="shrink-0 w-5 h-5 text-gray-400">
            {isSearching ? (
              <SpinnerIcon className="w-5 h-5 text-black" />
            ) : (
              <svg
                viewBox="0 0 12 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  d="M11.4033 11.2273L8.96475 8.78927C8.84486 8.66985 8.68253 8.6028 8.5133 8.6028C8.34407 8.6028 8.18173 8.66985 8.06184 8.78927C7.41927 9.41044 6.58876 9.80096 5.70041 9.89964C4.81207 9.99832 3.91607 9.7996 3.15279 9.33459C2.38952 8.86959 1.80208 8.16458 1.48252 7.33001C1.16296 6.49544 1.12932 5.57846 1.38688 4.72273C1.64443 3.867 2.17863 3.12086 2.90579 2.6012C3.63294 2.08154 4.51197 1.81773 5.40516 1.85108C6.29836 1.88444 7.15525 2.21308 7.8416 2.78553C8.52796 3.35797 9.005 4.14187 9.19798 5.01443C9.33627 5.64745 9.32203 6.30428 9.15642 6.93071C9.13686 7.00408 9.13195 7.08059 9.14196 7.15586C9.15197 7.23113 9.17671 7.30369 9.21478 7.3694C9.25284 7.43511 9.30347 7.49268 9.36379 7.53883C9.4241 7.58497 9.49292 7.61879 9.56631 7.63835C9.71452 7.67784 9.87235 7.65685 10.0051 7.58C10.1378 7.50315 10.2346 7.37672 10.2741 7.22854C10.5922 6.03301 10.474 4.76326 9.94055 3.64701C9.40711 2.53075 8.49336 1.64101 7.36318 1.13734C6.233 0.633672 4.96032 0.549027 3.77337 0.898584C2.58642 1.24814 1.56285 2.00903 0.886219 3.04481C0.209588 4.08058 -0.0758406 5.32348 0.0811204 6.55061C0.238081 7.77774 0.827164 8.90883 1.74273 9.74103C2.65829 10.5732 3.84044 11.0521 5.07717 11.0918C6.31391 11.1314 7.52433 10.7293 8.49136 9.9575L10.5858 12.0492C10.6395 12.103 10.7032 12.1456 10.7733 12.1747C10.8434 12.2038 10.9186 12.2188 10.9946 12.2188C11.0705 12.2188 11.1457 12.2038 11.2158 12.1747C11.286 12.1456 11.3497 12.103 11.4033 12.0492C11.4578 11.9955 11.501 11.9316 11.5305 11.861C11.56 11.7905 11.5752 11.7148 11.5752 11.6383C11.5752 11.5618 11.56 11.4861 11.5305 11.4156C11.501 11.345 11.4578 11.281 11.4033 11.2273Z"
                  fill="#8F8F8F"
                />
              </svg>
            )}
          </div>

          <input
            ref={inputRef}
            type="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="flex-1 text-base sm:text-lg bg-transparent outline-hidden placeholder-gray-400 text-black min-w-0"
            placeholder="Søk i apoteket..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleInputKeyDown}
            aria-label="Søkefelt"
          />

          {searchTerm ? (
            <button
              onClick={() => {
                setSearchTerm("");
                inputRef.current?.focus();
              }}
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
              aria-label="Tøm søk"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ) : null}

          <button
            onClick={onClose}
            className="shrink-0 text-xs font-semibold text-gray-500 hover:text-gray-900 border border-gray-200 rounded-sm px-1.5 py-0.5 transition-colors ml-1 hidden sm:block"
            aria-label="Lukk søk"
          >
            ESC
          </button>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 sm:hidden"
            aria-label="Lukk søk"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* ── Scrollable content ────────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 overscroll-contain">
          {/* Zero state – popular searches */}
          {showZeroState && (
            <div className="p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                Populære søk
              </p>
              <div className="flex flex-wrap gap-2">
                {(popularSearchTerms ?? []).map((term) => (
                  <button
                    key={term}
                    onClick={() => setSearchTerm(term)}
                    className="bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results list */}
          {showResults && (
            <ul role="listbox">
              {results.map((hit, idx) => {
                const isProduct = hit.type === "product";
                const { default: defaultPrice, sales: salesPrice } =
                  getPrice(hit);
                const isSelected = idx === selectedIndex;
                const sku = hit.matchingVariant?.sku ?? "";
                const cartState = addingToCart[sku] ?? "idle";
                const imgUrl = hit.matchingVariant?.images?.[0]?.url;

                return (
                  <li
                    key={hit.path}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer group transition-colors ${
                      isSelected ? "bg-gray-50" : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleNavigate(hit.path)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    {/* Thumbnail */}
                    <div
                      className={`shrink-0 w-12 h-12 rounded-lg border bg-white flex items-center justify-center overflow-hidden transition-colors ${
                        isSelected ? "border-gray-300" : "border-gray-100"
                      }`}
                    >
                      {imgUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={imgUrl}
                          alt={hit.name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                          width={48}
                          height={48}
                        />
                      ) : (
                        <svg
                          className="w-6 h-6 text-gray-300"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M4 4h16v12H4V4zm2 2v8h12V6H6zm-2 12h16v2H4v-2z" />
                        </svg>
                      )}
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-snug text-gray-900">
                        <HighlightedText
                          text={hit.name}
                          highlight={searchTerm}
                        />
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <TypeLabel type={hit.type} />
                        {salesPrice !== undefined && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-red-600 px-1.5 py-0.5 rounded-sm">
                            Tilbud
                          </span>
                        )}
                        {isProduct && defaultPrice !== undefined && (
                          <div className="flex items-center gap-1.5 border-l border-gray-200 pl-2">
                            {salesPrice !== undefined ? (
                              <>
                                <span className="text-sm font-bold text-red-600">
                                  {salesPrice} kr
                                </span>
                                <span className="text-xs text-gray-400 line-through">
                                  {defaultPrice} kr
                                </span>
                              </>
                            ) : (
                              <span className="text-sm font-bold text-gray-900">
                                {defaultPrice} kr
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action: cart button for products, chevron for others */}
                    {isProduct && sku ? (
                      <button
                        onClick={(e) => handleQuickAdd(e, hit)}
                        disabled={cartState === "loading"}
                        className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all ${
                          cartState === "success"
                            ? "bg-green-50 border-green-400 text-green-600"
                            : cartState === "loading"
                              ? "bg-gray-50 border-gray-200 text-gray-400 cursor-wait"
                              : "bg-white border-gray-200 hover:border-black hover:bg-black hover:text-white text-gray-600 opacity-0 group-hover:opacity-100"
                        } ${isSelected ? "opacity-100" : ""}`}
                        aria-label="Legg i handlekurv"
                        title="Legg i handlekurv"
                      >
                        {cartState === "loading" ? (
                          <SpinnerIcon className="w-4 h-4" />
                        ) : cartState === "success" ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <CartIcon className="w-4 h-4" />
                        )}
                      </button>
                    ) : (
                      <svg
                        className={`shrink-0 w-4 h-4 transition-colors ${isSelected ? "text-gray-600" : "text-gray-300 group-hover:text-gray-600"}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 18l6-6-6-6"
                        />
                      </svg>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* No results */}
          {showNoResults && (
            <div className="py-14 px-6 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  viewBox="0 0 12 13"
                  fill="none"
                  className="w-6 h-6 text-gray-400"
                >
                  <path
                    d="M11.4033 11.2273L8.96475 8.78927C8.84486 8.66985 8.68253 8.6028 8.5133 8.6028C8.34407 8.6028 8.18173 8.66985 8.06184 8.78927C7.41927 9.41044 6.58876 9.80096 5.70041 9.89964C4.81207 9.99832 3.91607 9.7996 3.15279 9.33459C2.38952 8.86959 1.80208 8.16458 1.48252 7.33001C1.16296 6.49544 1.12932 5.57846 1.38688 4.72273C1.64443 3.867 2.17863 3.12086 2.90579 2.6012C3.63294 2.08154 4.51197 1.81773 5.40516 1.85108C6.29836 1.88444 7.15525 2.21308 7.8416 2.78553C8.52796 3.35797 9.005 4.14187 9.19798 5.01443C9.33627 5.64745 9.32203 6.30428 9.15642 6.93071C9.13686 7.00408 9.13195 7.08059 9.14196 7.15586C9.15197 7.23113 9.17671 7.30369 9.21478 7.3694C9.25284 7.43511 9.30347 7.49268 9.36379 7.53883C9.4241 7.58497 9.49292 7.61879 9.56631 7.63835C9.71452 7.67784 9.87235 7.65685 10.0051 7.58C10.1378 7.50315 10.2346 7.37672 10.2741 7.22854C10.5922 6.03301 10.474 4.76326 9.94055 3.64701C9.40711 2.53075 8.49336 1.64101 7.36318 1.13734C6.233 0.633672 4.96032 0.549027 3.77337 0.898584C2.58642 1.24814 1.56285 2.00903 0.886219 3.04481C0.209588 4.08058 -0.0758406 5.32348 0.0811204 6.55061C0.238081 7.77774 0.827164 8.90883 1.74273 9.74103C2.65829 10.5732 3.84044 11.0521 5.07717 11.0918C6.31391 11.1314 7.52433 10.7293 8.49136 9.9575L10.5858 12.0492C10.6395 12.103 10.7032 12.1456 10.7733 12.1747C10.8434 12.2038 10.9186 12.2188 10.9946 12.2188C11.0705 12.2188 11.1457 12.2038 11.2158 12.1747C11.286 12.1456 11.3497 12.103 11.4033 12.0492C11.4578 11.9955 11.501 11.9316 11.5305 11.861C11.56 11.7905 11.5752 11.7148 11.5752 11.6383C11.5752 11.5618 11.56 11.4861 11.5305 11.4156C11.501 11.345 11.4578 11.281 11.4033 11.2273Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <p className="font-bold text-gray-900 mb-1">
                Ingen treff på «{searchTerm}»
              </p>
              <p className="text-sm text-gray-500">
                Prøv å sjekke stavingen eller bruk færre ord.
              </p>
            </div>
          )}
        </div>

        {/* ── Show all footer ───────────────────────────────────────────── */}
        {showResults && (
          <div className="shrink-0 border-t border-gray-100 p-3">
            <button
              onClick={handleShowAll}
              onMouseEnter={() => setSelectedIndex(results.length)}
              className={`w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-colors border ${
                selectedIndex === results.length
                  ? "bg-black text-white border-black"
                  : "bg-white text-gray-800 border-gray-200 hover:border-gray-800 hover:bg-gray-50"
              }`}
            >
              Se alle resultater for «{searchTerm}»
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 18l6-6-6-6"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
